'use client'

import { useCallback, useEffect, useState } from 'react'
import { MdDoneAll, MdNotificationsActive } from 'react-icons/md'
import { useNavigate } from 'react-router-dom'

import {
    getNotifications,
    markAllAsRead,
    markAsRead,
    type Notification,
} from '@/lib/api/notifications/notification-api'
import { useChatOverlay } from '@/lib/hooks/use-chat-overlay'

interface MyAlertsModalProps {
    isOpen: boolean
    onClose: () => void
}

/**
 * Todos os alertas pessoais, paginados.
 *
 * A paginação vem do SERVIDOR: carregar tudo e paginar aqui mentiria sobre o total e,
 * com meses de histórico, travaria a tela justamente de quem mais recebe alerta — que é
 * quem mais precisa dela.
 */
const PAGE_SIZE = 20

export default function MyAlertsModal({ isOpen, onClose }: MyAlertsModalProps) {
    const navigate = useNavigate()
    const openChatOverlay = useChatOverlay()
    const [items, setItems] = useState<Notification[]>([])
    const [total, setTotal] = useState(0)
    const [page, setPage] = useState(1)
    const [loading, setLoading] = useState(false)

    const load = useCallback(async (nextPage: number) => {
        setLoading(true)
        try {
            const response = await getNotifications({
                scope: 'PERSONAL',
                page: nextPage,
                limit: PAGE_SIZE,
            })
            if (response.success) {
                setItems(response.result?.notifications ?? [])
                setTotal(response.result?.total ?? 0)
                setPage(nextPage)
            }
        } finally {
            setLoading(false)
        }
    }, [])

    useEffect(() => {
        // Reabrir sempre começa na primeira página: quem fechou na página 4 não quer
        // voltar para lá, quer ver o que chegou.
        if (isOpen) void load(1)
    }, [isOpen, load])

    if (!isOpen) return null

    const lastPage = Math.max(1, Math.ceil(total / PAGE_SIZE))

    const open = async (item: Notification) => {
        if (!item.readAt) await markAsRead(item.id)
        if (item.link) {
            onClose()
            // Mesmo critério do painel: conversa abre sobreposta pelo dock; o resto
            // navega.
            if (!openChatOverlay(item.link)) navigate(item.link)
        } else {
            void load(page)
        }
    }

    const readAll = async () => {
        await markAllAsRead()
        void load(page)
    }

    return (
        <dialog className="modal modal-open z-[70]">
            <div className="modal-box !max-w-2xl">
                <div className="flex items-center justify-between gap-3 mb-4">
                    <h3 className="text-lg font-bold flex items-center gap-2">
                        <MdNotificationsActive className="text-secondary" /> Meus Alertas
                    </h3>
                    <button
                        type="button"
                        className="btn btn-ghost btn-sm gap-2"
                        onClick={() => void readAll()}
                    >
                        <MdDoneAll /> Marcar todas como lidas
                    </button>
                </div>

                {loading ? (
                    <div className="flex justify-center py-12">
                        <span className="loading loading-spinner loading-md text-secondary" />
                    </div>
                ) : items.length === 0 ? (
                    <p className="py-12 text-center text-sm text-base-content/50">
                        Nenhum alerta para você.
                    </p>
                ) : (
                    <ul className="divide-y divide-base-200 max-h-[60vh] overflow-y-auto">
                        {items.map(item => (
                            <li key={item.id}>
                                <button
                                    type="button"
                                    className="w-full text-left py-3 px-1 hover:bg-base-200/60 rounded"
                                    onClick={() => void open(item)}
                                >
                                    <span className="flex items-center gap-2">
                                        {!item.readAt && (
                                            <span className="h-2 w-2 flex-shrink-0 rounded-full bg-secondary" />
                                        )}
                                        <span className="text-sm font-medium">{item.title}</span>
                                        <span className="ml-auto text-[11px] text-base-content/40 flex-shrink-0">
                                            {new Date(item.createdAt).toLocaleString('pt-BR')}
                                        </span>
                                    </span>
                                    <span className="block text-xs text-base-content/60 mt-0.5">
                                        {item.message}
                                    </span>
                                </button>
                            </li>
                        ))}
                    </ul>
                )}

                <div className="modal-action items-center">
                    <span className="mr-auto text-xs text-base-content/50">
                        {total} alerta(s) · página {page} de {lastPage}
                    </span>
                    <button
                        type="button"
                        className="btn btn-sm btn-ghost"
                        disabled={page <= 1 || loading}
                        onClick={() => void load(page - 1)}
                    >
                        Anterior
                    </button>
                    <button
                        type="button"
                        className="btn btn-sm btn-ghost"
                        disabled={page >= lastPage || loading}
                        onClick={() => void load(page + 1)}
                    >
                        Próxima
                    </button>
                    <button type="button" className="btn btn-sm" onClick={onClose}>
                        Fechar
                    </button>
                </div>
            </div>
        </dialog>
    )
}
